import type { Project, ProjectTask } from '../types';

/**
 * ⭐ Milestones ARE tasks (owner call 2026-09-16).
 *
 * A milestone used to be its own record in `Project.milestones`, with its own
 * editor, its own store actions and no phase of its own. It is now a flag on a
 * task — `ProjectTask.isMilestone` — so one thing can be both "the work" and
 * "the marker", and every surface that already understands tasks understands
 * milestones for free.
 *
 * `convertLegacyMilestones` is the one-way door. It runs wherever a project
 * ENTERS the store — the persisted migration, the cloud merge, and the shared
 * copy a member reads — because a project can arrive from any of the three and
 * an unconverted one would render an empty Timeline lane with its milestones
 * nowhere to be seen.
 *
 * ⚠️ The milestone's id becomes the task's id. Selections, the R4b activity log
 * and anything else holding an id keep resolving to the same thing.
 */
export function milestoneTasks(project: Project): ProjectTask[] {
  return project.tasks.filter(task => task.isMilestone);
}

/** True when this project still carries pre-conversion milestone records. */
export function hasLegacyMilestones(project: Project): boolean {
  return (project.milestones?.length ?? 0) > 0;
}

export function convertLegacyMilestones(project: Project): Project {
  if (!hasLegacyMilestones(project)) {
    // Idempotent AND reference-stable: the merge paths compare by identity to
    // decide whether to write back to the cloud, so a no-op must return `project`.
    return project;
  }
  const fallbackPhase = project.phases[0]?.id ?? '';
  const nextOrder = new Map<string, number>();
  for (const task of project.tasks) {
    nextOrder.set(task.phaseId, Math.max(nextOrder.get(task.phaseId) ?? -1, task.order) + 1);
  }
  const converted = project.milestones.map((milestone): ProjectTask => {
    const phaseId = milestone.phaseId && project.phases.some(phase => phase.id === milestone.phaseId)
      ? milestone.phaseId
      : fallbackPhase;
    const order = nextOrder.get(phaseId) ?? 0;
    nextOrder.set(phaseId, order + 1);
    return {
      id: milestone.id,
      title: milestone.title,
      description: milestone.description,
      completed: milestone.completed,
      // A finished milestone lands as a finished task, so "Tasks done" counts it
      // the way the owner asked. No pointRecord is invented: XP is minted on the
      // transition, and this one already happened.
      status: milestone.completed ? 'done' : 'todo',
      priority: 'medium',
      phaseId,
      order,
      dueDate: milestone.dueDate,
      pointRecord: milestone.pointRecord,
      isMilestone: true,
    };
  });
  return { ...project, tasks: [...project.tasks, ...converted], milestones: [] };
}

/** Convert a whole collection, keeping the array identity when nothing changed. */
export function convertLegacyMilestonesIn(projects: Project[]): Project[] {
  if (!projects.some(hasLegacyMilestones)) return projects;
  return projects.map(convertLegacyMilestones);
}
